import React, { useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import {
  SettingsCard,
  SettingsCards,
  SettingsFooter,
  SettingsNote,
  SettingsPageHeader,
} from '../SettingsShell/SettingsPage';
import { SetAvatar } from '../SettingsShell/controls';
import { ReasonDrawer } from './ReasonDrawer';
import { AddSentenceModal } from './AddSentenceModal';
import {
  BOOKABLE_LABEL,
  CATEGORY_COLOUR,
  CATEGORY_LABEL,
  REASON_CATEGORIES,
  SENTENCE_SOURCE_LABEL,
} from '../../settings-booking/types';
import type { AppointmentReason, ReasonCategory, ReasonsData, ReasonSentence } from '../../settings-booking/types';
import { ROLE_LABEL } from '../../settings-practice/types';
import type { Provider, Role } from '../../settings-practice/types';

export interface ReasonsViewProps {
  data: ReasonsData;
  /** The roster — the faces beside each role are derived from it, never chosen (§8.1). */
  providers: Provider[];
  location: string;
  onOpenTrainElva?: () => void;
  /** Persist a reason saved from the drawer. Sentences stay local — there is no API for them. */
  onSaveReason?: (next: AppointmentReason) => void | Promise<void>;
}

/** Appointment & booking › Appointment reasons (spec 15 §8). */
export function ReasonsView({ data, providers, location, onOpenTrainElva, onSaveReason }: ReasonsViewProps) {
  const [reasons, setReasons] = useState(data.reasons);
  const [sentences, setSentences] = useState(data.sentences);
  const [category, setCategory] = useState<'all' | ReasonCategory>('all');
  const [editing, setEditing] = useState<AppointmentReason | null>(null);
  const [addingFor, setAddingFor] = useState<AppointmentReason | null | 'any'>(null);
  const [showAllSentences, setShowAllSentences] = useState(false);

  /** Who holds a role right now — a new hygienist takes cleanings the day she appears. */
  const holders = useMemo(() => {
    const map = new Map<Role, Provider[]>();
    for (const p of providers) {
      for (const r of p.roles) map.set(r, [...(map.get(r) ?? []), p]);
    }
    return map;
  }, [providers]);

  const shown = category === 'all' ? reasons : reasons.filter((r) => r.category === category);

  // Every count is computed from the same array that draws the rows (§3.1).
  const catCount = (c: ReasonCategory) => reasons.filter((r) => r.category === c).length;
  const usedCategories = REASON_CATEGORIES.filter((c) => catCount(c.id) > 0);
  const anyone = reasons.filter((r) => r.bookableBy === 'anyone').length;
  const elvaStaff = reasons.filter((r) => r.bookableBy === 'elva_and_staff').length;
  const staffOnly = reasons.filter((r) => r.bookableBy === 'staff_only').length;
  const emptyRole = reasons.filter((r) => r.roles.some((role) => (holders.get(role) ?? []).length === 0)).length;
  const noCdt = reasons.filter((r) => r.cdtCodes.length === 0).length;
  const withCondition = sentences.filter((s) => s.condition).length;

  /** The check is `no sentence` AND `ELVA or a patient can book it` — not "how many" (§8.2). */
  const uncovered = reasons.filter(
    (r) => r.bookableBy !== 'staff_only' && !sentences.some((s) => s.reasonId === r.id),
  );

  const visibleSentences = showAllSentences ? sentences : sentences.slice(0, 8);
  const reasonById = (id: string) => reasons.find((r) => r.id === id);

  return (
    <div>
      <SettingsPageHeader
        eyebrow="Settings · Appointment & booking"
        titleLead="Appointment"
        titleAccent="reasons"
        sub={
          <>
            The visits patients can book.
          </>
        }
      />

      <SettingsCards>
        {/* 01 — reasons */}
        <SettingsCard
          n="01"
          title="Reasons"
          sub={
            <>
              {reasons.length} visit types. Each sets how long it takes, which role does it and who can book it.
            </>
          }
        >
          <div className="flex flex-wrap items-center gap-2 px-6 pt-5">
            <button
              type="button"
              onClick={() => { setCategory('all'); }}
              className={cn(
                'rounded-full px-3 py-1 text-[12px] font-bold',
                category === 'all'
                  ? 'bg-[var(--text-primary)] text-white'
                  : 'bg-[var(--surface-sunken)] text-[var(--text-secondary)] hover:bg-[var(--surface-header)]',
              )}
            >
              All {reasons.length}
            </button>
            {usedCategories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => { setCategory(c.id); }}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold',
                  category === c.id
                    ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]'
                    : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
                )}
              >
                <span className="h-2 w-2 rounded-full" style={{ background: c.colour }} />
                {c.label}
                <span className="text-[var(--text-muted)]">{catCount(c.id)}</span>
              </button>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-[minmax(0,1.5fr)_70px_minmax(0,1.5fr)_120px_34px] gap-x-4 bg-[var(--surface-sunken)] px-6 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
            <span>Reason</span>
            <span>Length</span>
            <span>Who can do it</span>
            <span>Who can book it</span>
            <span />
          </div>

          {shown.map((r) => (
            <ReasonRow key={r.id} r={r} holders={holders} onEdit={() => { setEditing(r); }} />
          ))}

          <SettingsNote>
            A reason goes to a role, not a person. {emptyRole} reasons name a role nobody holds; they still book because
            another role covers them.
          </SettingsNote>
        </SettingsCard>

        {/* 02 — what patients say */}
        <SettingsCard
          n="02"
          accent="amber"
          title="What patients say"
          sub={
            <>
              What patients say, and the reason it means.
            </>
          }
          headRight={
            <span className="rounded-full bg-[#F1F3F9] px-2.5 py-1 text-[11px] font-bold text-[var(--text-muted)]">
              {sentences.length}
            </span>
          }
        >
          <div className="px-6 pt-5">
            <div className="text-[13px] font-bold text-[var(--text-primary)]">
              Sentences, each mapped to a reason by a person
            </div>
          </div>

          <div className="mt-3">
            {visibleSentences.map((s) => (
              <SentenceRow key={s.id} s={s} reason={reasonById(s.reasonId)} />
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
            {sentences.length > 8 ? (
              <button
                type="button"
                onClick={() => { setShowAllSentences((v) => !v); }}
                className="text-[12.5px] font-bold text-[var(--brand-indigo)] hover:underline"
              >
                {showAllSentences ? 'Show the longest-standing 8 ←' : `Show all ${String(sentences.length)} →`}
              </button>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={() => { setAddingFor('any'); }}
              className="inline-flex items-center gap-1.5 rounded-[10px] border border-[var(--border-default)] px-3 py-2 text-[12px] font-semibold text-[var(--brand-indigo)] hover:bg-[var(--surface-sunken)]"
            >
              <Icon kind="plus" size={12} stroke={2.4} /> Add a sentence
            </button>
          </div>

          <SettingsNote tone="amber">
            {withCondition} of the {sentences.length} have an &ldquo;only when&rdquo; condition.
          </SettingsNote>

          {uncovered.length > 0 ? (
            <SettingsNote>
              {uncovered.length} reason has no sentence: {uncovered.map((r) => r.displayName).join(', ')}.
            </SettingsNote>
          ) : null}

          {/* The rows live on Train ELVA; this page keeps the count and a link (§15). */}
          <div className="m-6 rounded-[12px] bg-[#EEF6F2] p-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[15px] font-bold text-[var(--money-green)]">
                  {data.waitingFromCalls} sentences from real calls are waiting for somebody to say yes or no
                </div>
                <p className="m-0 mt-1.5 max-w-[620px] text-[12.5px] leading-[1.65] text-[#1B5F45]">
                  Review them on Train ELVA.
                </p>
              </div>
              <button
                type="button"
                onClick={onOpenTrainElva}
                className="shrink-0 rounded-[10px] bg-[var(--brand-indigo)] px-4 py-2.5 text-[13.5px] font-semibold text-white"
              >
                Open Train ELVA →
              </button>
            </div>
          </div>
        </SettingsCard>
      </SettingsCards>

      <SettingsFooter
        location={location}
        left={
          <>
            {reasons.length} reasons in {usedCategories.length} categories · {anyone} anyone, {elvaStaff} ELVA &amp;
            staff, {staffOnly} staff only · {emptyRole} name an empty role · {noCdt} have no CDT code
            <br />
            {sentences.length} sentences mapped by hand, {withCondition} with a condition · {uncovered.length} reasons
            have none · {data.waitingFromCalls} waiting from real calls
          </>
        }
      />

      <ReasonDrawer
        reason={editing}
        holders={holders}
        sentences={sentences.filter((s) => s.reasonId === editing?.id)}
        onClose={() => { setEditing(null); }}
        onSave={(next) => {
          const apply = () => {
            setReasons((prev) => prev.map((x) => (x.id === next.id ? next : x)));
            setEditing(null);
          };
          if (!onSaveReason) {
            apply();
            return;
          }
          void Promise.resolve(onSaveReason(next)).then(apply, () => undefined);
        }}
        onAddSentence={() => {
          if (editing) setAddingFor(editing);
        }}
      />

      <AddSentenceModal
        open={addingFor !== null}
        reasons={reasons}
        holders={holders}
        initialReason={addingFor === 'any' ? null : addingFor}
        onClose={() => { setAddingFor(null); }}
        onAdd={(s) => {
          setSentences((prev) => [...prev, s]);
          setAddingFor(null);
        }}
      />
    </div>
  );
}

function ReasonRow({
  r,
  holders,
  onEdit,
}: {
  r: AppointmentReason;
  holders: Map<Role, Provider[]>;
  onEdit: () => void;
}) {
  const colour = CATEGORY_COLOUR[r.category];
  // The offered set is the union of the holders of every role the reason names.
  const faces = Array.from(new Set(r.roles.flatMap((role) => holders.get(role) ?? [])));
  return (
    <div
      className="grid grid-cols-[minmax(0,1.5fr)_70px_minmax(0,1.5fr)_120px_34px] items-center gap-x-4 border-t border-[var(--border-default)] px-6 py-3.5"
    >
      <span className="flex min-w-0 items-start gap-3">
        <span
          className="mt-0.5 shrink-0 rounded-[6px] px-1.5 py-0.5 font-[var(--font-mono)] text-[10px] font-bold"
          style={{ background: `${colour}1A`, color: colour }}
        >
          {r.code}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-[13.5px] font-semibold text-[var(--text-primary)]">
            {r.displayName}
          </span>
          <span className="block truncate text-[11px] text-[var(--text-muted)]">
            {CATEGORY_LABEL[r.category]}
            {r.cdtCodes.length > 0 ? (
              <>
                {' · '}
                <span className="font-[var(--font-mono)]">{r.cdtCodes.join(', ')}</span>
              </>
            ) : null}
          </span>
          {/* An inline flag only where the absence costs something (§8.1). */}
          {r.cdtCodes.length === 0 ? (
            <span className="mt-0.5 block text-[11px] font-semibold text-[var(--warn-amber)]">
              no CDT code — nothing downstream knows this happened
            </span>
          ) : null}
        </span>
      </span>

      <span className="text-[13px] font-semibold text-[var(--text-primary)]">
        {r.durationMinutes} <span className="text-[11px] font-normal text-[var(--text-muted)]">min</span>
      </span>

      <span className="flex min-w-0 flex-wrap items-center gap-1.5">
        {r.roles.map((role) => {
          const empty = (holders.get(role) ?? []).length === 0;
          return (
            <span
              key={role}
              className={cn(
                'rounded-full px-2 py-1 text-[11px] font-semibold',
                empty ? 'bg-[#FBF3E4] text-[var(--warn-amber)]' : 'bg-[#F1F3F9] text-[var(--text-secondary)]',
              )}
            >
              {ROLE_LABEL[role]}
              {empty ? ' · nobody' : ''}
            </span>
          );
        })}
        <span className="ml-1 flex items-center -space-x-1">
          {faces.map((f) => (
            <SetAvatar key={f.id} initials={f.initials} colour={f.colour} size={20} />
          ))}
        </span>
      </span>

      <span>
        <BookablePill value={r.bookableBy} />
      </span>

      <span className="flex justify-end">
        <button
          type="button"
          aria-label={`Edit ${r.displayName}`}
          onClick={onEdit}
          className="flex h-7 w-7 items-center justify-center rounded-[8px] text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]"
        >
          <Icon kind="pencil" size={13} stroke={2} />
        </button>
      </span>
    </div>
  );
}

export function BookablePill({ value }: { value: AppointmentReason['bookableBy'] }) {
  const tone =
    value === 'anyone'
      ? 'bg-[#EEF6F2] text-[var(--money-green)]'
      : value === 'elva_and_staff'
        ? 'bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]'
        : 'bg-[#F1F3F9] text-[var(--text-muted)]';
  return (
    <span className={cn('inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold', tone)}>
      {BOOKABLE_LABEL[value]}
    </span>
  );
}

function SentenceRow({ s, reason }: { s: ReasonSentence; reason: AppointmentReason | undefined }) {
  const colour = reason ? CATEGORY_COLOUR[reason.category] : '#98A0B2';
  return (
    <div
      className="flex items-start justify-between gap-6 px-6 py-3"
      style={{ borderTop: '1px solid #edf0f6' }}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px] text-[var(--text-primary)]">&ldquo;{s.text}&rdquo;</span>
        {s.condition ? (
          <span className="mt-0.5 block text-[11.5px] text-[var(--warn-amber)]">
            <strong>only when</strong> {s.condition}
          </span>
        ) : null}
        <span className="mt-1 block text-[10.5px] text-[var(--text-muted)]">
          <span
            className={cn(
              'rounded-full px-1.5 py-0.5 font-bold',
              s.source === 'corrected_from_call'
                ? 'bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]'
                : s.source === 'confirmed_from_call'
                  ? 'bg-[#EEF6F2] text-[var(--money-green)]'
                  : 'bg-[#F1F3F9] text-[var(--text-muted)]',
            )}
          >
            {SENTENCE_SOURCE_LABEL[s.source]}
          </span>{' '}
          · {s.author}
        </span>
      </span>
      {reason ? (
        <span
          className="flex shrink-0 items-center gap-2 rounded-[8px] px-2 py-1.5"
          style={{ background: `${colour}14` }}
        >
          <span
            className="rounded-[5px] px-1.5 py-0.5 font-[var(--font-mono)] text-[9.5px] font-bold"
            style={{ background: `${colour}26`, color: colour }}
          >
            {reason.code}
          </span>
          <span className="text-[12px] font-bold text-[var(--text-primary)]">{reason.displayName}</span>
        </span>
      ) : null}
    </div>
  );
}
